import { SegmentedTabs, type SegTab } from '../SegmentedTabs'

/**
 * "When Langy Went Off-Script": the three deliberate testing layers as an
 * explorable stack instead of a markdown table. Each layer's coverage
 * visibly builds on the one before it; the gap column is the actual argument
 * for the next layer existing at all. Dogfooding isn't here on purpose: it's
 * not a technique you run, it's what tells you which of these three to run.
 */

type Layer = {
  id: string
  label: string
  is: string
  catches: string
  misses: string
  fill: number // 0-100, how much of the "coverage" bar this layer visibly closes
}

const LAYERS: Layer[] = [
  {
    id: 'evals',
    label: 'Evals',
    is: 'Fixed input, scored against a rubric.',
    catches: 'Regressions on cases you already thought of.',
    misses: 'Anything nobody thought to ask.',
    fill: 25,
  },
  {
    id: 'scenarios',
    label: 'Scenarios',
    is: 'Simulated multi-turn conversation, judged by rubric.',
    catches: 'Known classes of interaction, across turns.',
    misses: "What a motivated human does that the scenario author didn't imagine.",
    fill: 55,
  },
  {
    id: 'redteam',
    label: 'Red team',
    is: 'Scenarios run adversarially, escalating on purpose.',
    catches: 'Multi-turn manipulation nobody would test for directly.',
    misses: "Only what the attacker model itself won't try.",
    fill: 100,
  },
]

function LayerPanel({ layer }: { layer: Layer }) {
  return (
    <div className="animate-fade-in rounded-2xl border border-ink-100 bg-paper">
      <div className="border-b border-ink-100 px-5 py-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
          Cumulative coverage
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-ink-100">
          <span
            className="block h-full rounded-full transition-all duration-500"
            style={{
              width: `${layer.fill}%`,
              backgroundImage: 'linear-gradient(90deg, #c97b3a, #b85240, #c75d97)',
            }}
          />
        </div>
      </div>
      <dl className="divide-y divide-ink-100">
        <Row term="Is" desc={layer.is} />
        <Row term="Catches" desc={layer.catches} tone="catches" />
        <Row term="Misses" desc={layer.misses} tone="misses" />
      </dl>
    </div>
  )
}

function Row({ term, desc, tone }: { term: string; desc: string; tone?: 'catches' | 'misses' }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-3 px-5 py-4 sm:grid-cols-[100px_1fr]">
      <dt
        className={
          tone === 'catches'
            ? 'font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-amber-accent'
            : tone === 'misses'
              ? 'font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-400'
              : 'font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-600'
        }
      >
        {term}
      </dt>
      <dd className="text-[14.5px] leading-[1.55] text-ink-700">{desc}</dd>
    </div>
  )
}

export function OffScriptTaxonomy() {
  const tabs: SegTab[] = LAYERS.map((layer) => ({
    id: layer.id,
    label: layer.label,
    render: () => <LayerPanel layer={layer} />,
  }))

  return <SegmentedTabs tabs={tabs} className="not-prose my-8" />
}
